<template>
  <router-link class="ui card host" :class="[oldRole, newRole]" :to="url" :event="hasSelector ? '' : 'click'" @click.native="select()">
    <div class="ui buttons selector" v-if="hasSelector && isSelectable">
      <div class="ui button role1" :class="[selectorButtons[0].color, {disabled: isRole1Disabled}]" @click.stop.prevent="select(selector[0])">
        {{selectorButtons[0].text}}
      </div>
      <div class="or" v-if="hasDifferentRoles" />
      <div class="ui button role2" :class="[selectorButtons[1].color, {disabled: isRole2Disabled}]" v-if="hasDifferentRoles" @click.stop.prevent="select(selector[1])">
        {{selectorButtons[1].text}}
      </div>
    </div>
    <div class="ui buttons" v-if="hasSelector && !isSelectable">
      <div class="ui disabled button">Can't be selected</div>
    </div>
    <sui-card-content class="compact">
      <div class="ui mini right floated buttons">
        <div class="ui icon button" data-inverted :data-tooltip="localAccessTitle" data-goal="HOST_CARD_ACCESS" v-if="account.limits.invite && access.tariffManagement" @click.stop.prevent="$modals.entityAccess.open(host)">
          <i class="icon" :class="localAccessIcon" />
        </div>
        <div class="ui icon button" data-inverted data-tooltip="Update D2C Health Checker" v-if="healthStatus.needUpdate">
          <i class="orange exclamation icon" />
        </div>
        <div class="ui icon button" data-inverted data-tooltip="Need to reboot" v-if="healthStatus.needReboot">
          <i class="orange exclamation icon" />
        </div>
        <div class="ui icon button" data-inverted data-tooltip="Docker error" v-if="healthStatus.dockerError">
          <i class="red exclamation icon" />
        </div>
        <div class="ui icon button" data-inverted data-tooltip="Weave error" v-if="healthStatus.weaveError" @click.stop.prevent="$modals.log.open({target: 'weave', id: host.id})">
          <i class="red exclamation icon" />
        </div>
      </div>
      <sui-card-header>
        {{host.name}}
      </sui-card-header>
      <sui-card-meta :title="host.demo ? host.demoExpires : host.created">
        {{host.demo ? 'will be destroyed' : 'created'}}
        {{momentFromNow(host.demo ? host.demoExpires : host.created)}}
      </sui-card-meta>
    </sui-card-content>
    <sui-card-content class="compact" :style="{background}">
      <Logo class="right floated logo" :entity="host" />
      <p class="provider-info">
        <span v-if="host.ipAddress"><b>{{host.ipAddress}}</b> <CopyIcon :value="host.ipAddress" data-goal="HOST_CARD_COPY_IP" /></span>
        <span v-if="!host.ipAddress">Unknown IP</span>
        <br>
        <span v-if="regionData.country"><sui-flag :name="regionData.country" :title="regionData.country" />{{regionData.city}}</span>
        <span v-if="!regionData.country">Unknown location</span>
      </p>
    </sui-card-content>
    <sui-card-content class="compact">
      <span v-if="!hasStats">No stats yet.</span>
      <Stats :entity="host" v-if="hasStats" />
    </sui-card-content>
    <sui-card-content class="ports" extra v-if="isRemoteService && visiblePorts.length">
      <span class="port" :class="{busy: port.busy}" v-for="(port, index) in visiblePorts" :key="index" :data-tooltip="`Project: ${port.projectName}\nService: ${port.serviceName}`" @click.prevent>
        <Logo :serviceType="port.serviceType" inline />{{port.protocol}}:{{port.value}}
      </span>
    </sui-card-content>
    <sui-card-content v-if="isRemoteService && !visiblePorts.length">
      No services with remote access
    </sui-card-content>
    <sui-button-group class="attached" size="tiny" fluid v-if="!selector.length">
      <sui-button icon="cog" title="Edit" data-goal="HOST_CARD_EDIT" @click.stop.prevent="$modals.updateHost.open(host)" />
      <sui-button :disabled="!actions.hostTerminal" data-goal="HOST_CARD_TERMINAL" @click.stop.prevent="$modals.terminal.open(host)">Terminal</sui-button>
      <sui-button :disabled="!actions.hostInfo" data-goal="HOST_CARD_METRICS" @click.stop.prevent="$modals.metrics.open(host)">Metrics</sui-button>
      <sui-dropdown class="icon" icon="ellipsis vertical" button :disabled="inProgress" @click.stop.prevent>
        <sui-dropdown-menu>
          <sui-dropdown-item icon="stop" text="Stop" :disabled="host.isCustom || !actions.hostUpdate" v-show="!isOffline" @click.stop.prevent="stop()" data-goal="HOST_CARD_STOP" />
          <sui-dropdown-item icon="refresh" text="Restart" :disabled="host.isCustom || !actions.hostUpdate" v-show="!isOffline" @click.stop.prevent="restart()" data-goal="HOST_CARD_RESTART" />
          <sui-dropdown-item icon="play" text="Start" :disabled="host.isCustom || !actions.hostUpdate" v-show="isOffline" @click.stop.prevent="start()" data-goal="HOST_CARD_START" />
          <sui-dropdown-divider />
          <sui-dropdown-item icon="file alternate outline" text="Log" :disabled="!actions.hostInfo" data-goal="HOST_CARD_LOGS" @click.stop.prevent="$modals.log.open(host)" />
          <sui-dropdown-item icon="download" text="Update system utils" :disabled="!actions.hostUpdate" data-goal="HOST_CARD_UPDATE_UTILS" @click.stop.prevent="updateSystem()" />
          <sui-dropdown-item icon="red trash" text="Destroy" :disabled="!actions.hostDestroy" @click.stop.prevent="destroy()" />
        </sui-dropdown-menu>
      </sui-dropdown>
    </sui-button-group>
    <EntityStatus :entity="host" extended />
  </router-link>
</template>

<script>
  import {mapGetters} from 'vuex'
  import Stats from '@/components/Stats'
  import EntityStatus from '@/components/EntityStatus'
  import utils from '@/utils'

  export default {
    name: 'HostCard',
    components: {
      Stats,
      EntityStatus,
    },
    props: {
      host: {
        type: Object,
        required: true,
      },
      container: {
        type: Object,
        required: false,
      },
      service: {
        type: Object,
        required: false,
        default() {
          return this.container ? this.container._service : null
        }
      },
      ports: {
        type: Array,
        required: false,
        default() {
          return []
        },
      },
    },
    computed: {
      ...mapGetters(['access', 'actionsById', 'account', 'config', 'projectsById', 'servicesById']),
      actions() {
        return this.actionsById[this.host.id]
      },
      url() {
        return this.host._path
      },
      background() {
        return this.config.colorCards ? `linear-gradient(135deg, transparent, ${this.host.constructor.color}22)` : 'transparent'
      },
      healthStatus() {
        const isSetup = this.host._isSetup
        const isOnline = this.host._isOnline
        const healthStatus = this.host.healthStatus
        return {
          needUpdate: isSetup && isOnline && !healthStatus,
          needReboot: isSetup && isOnline && healthStatus && healthStatus.needReboot,
          dockerError: isSetup && isOnline && healthStatus && !healthStatus.dockerOK,
          weaveError: isSetup && isOnline && healthStatus && !healthStatus.weaveOK,
        }
      },
      hasStats() {
        return Boolean(this.host._stats)
      },
      regionData() {
        return this.host._regionData
      },
      inProgress() {
        return this.host._inProgress
      },
      isOffline() {
        return this.host._isOffline
      },
      isRemoteService() {
        return this.service && this.service.remoteAccess
      },
      hasLocalRules() {
        return this.host.rules.local && Object.keys(this.host.rules.local).length
      },
      localAccessIcon() {
        return this.hasLocalRules ? 'blue users' : 'users'
      },
      localAccessTitle() {
        return this.hasLocalRules ? 'Has local access rules' : 'No local access rules'
      },
      visiblePorts() {
        if (!this.isRemoteService) {
          return []
        }
        const newPorts = this.service.ports.map(port => port.protocol + port.value)
        return this.ports.map(port => {
          const portProject = this.projectsById[port.project]
          const portService = this.servicesById[port.service] || {}
          return {
            ...port,
            projectName: portProject ? portProject.name : 'Unknown',
            serviceId: port.service,
            serviceName: port.serviceName || portService.name || 'Unknown',
            serviceType: port.serviceType || portService.type,
            serviceTypeTitle: portService._typeTitle || 'Unknown',
            busy: !this.oldRole && newPorts.includes(port.protocol + port.value),
          }
        })
      },
      hasBusyPort() {
        return Boolean(this.ports.find(port => port.busy))
      },
      selector() {
        return this.service ? this.service.constructor.fields.hosts.selector[this.service.configuration || 'default'] : []
      },
      hasSelector() {
        return Boolean(this.selector.length)
      },
      isSelectable() {
        const isHostBusy = this.host._inProgress && this.service && this.service._doesExist // only for created services (new services can wait)
        return !this.hasBusyPort && !isHostBusy && this.actions.hostUsage
      },
      hasDifferentRoles() {
        return this.selector.length > 1 && this.selector[0] !== this.selector[1]
      },
      oldRole() {
        const host = this.service ? this.service._hostRoles.find(host => host.id === this.host.id) : null
        return host ? host.role : null
      },
      newRole() {
        const containerHost = this.container ? this.container.hosts.find(host => host.id === this.host.id) : null
        const serviceHost = this.service ? this.service.hosts.find(host => host.id === this.host.id) : null
        const host = containerHost || serviceHost || {role: null}
        return host.role
      },
      hasCurrentRole1() {
        return Boolean(this.service._hostRoles.find(host => host.role === this.selector[0]))
      },
      isRole1Disabled() {
        const isDifferentContainerRole = this.container && this.hasDifferentRoles && this.container.role === this.selector[1]
        return (!this.hasBusyPort && this.hasDifferentRoles && (!this.container && this.hasCurrentRole1) || this.oldRole === this.selector[0]) || isDifferentContainerRole
      },
      isRole2Disabled() {
        const isDifferentContainerRole = this.container && this.hasDifferentRoles && this.container.role === this.selector[0]
        return (this.oldRole && !this.hasBusyPort) || isDifferentContainerRole
      },
      selectorButtons() {
        const roleColors = {
          node: 'teal',
          master: 'violet',
          slave: 'brown',
          current: 'grey',
        }
        return this.selector.map(role => {
          const isCurrent = this.oldRole === role
          const isSelected = this.newRole === role
          return {
            text: `${isCurrent ? 'Current' : (isSelected ? 'Selected' : 'Select')} ${role}`,
            color: isCurrent ? roleColors.current : (isSelected ? roleColors[this.newRole] : null),
          }
        })
      },
    },
    methods: {
      select(newRole) {
        if (!this.oldRole && this.isSelectable) {
          this.$emit('select', this.host, newRole)
        }
      },
      restart() {
        return this.$modals.confirm.open({
          text: 'You are going to restart the host.',
          callback: () => {
            this.host.restart().catch(error => {
              this.$emit('error', error.message)
            })
          },
        })
      },
      start() {
        return this.$modals.confirm.open({
          text: 'You are going to start the host.',
          callback: () => {
            this.host.start().catch(error => {
              this.$emit('error', error.message)
            })
          },
        })
      },
      stop() {
        return this.$modals.confirm.open({
          text: 'You are going to stop the host.<hr class="ui divider"><small><b>Pay attention!</b> When you stop your host, the other hosts start updating the network. The process usually takes 1-5 minutes, and some of the hosts might be unavailable during this time. So do not do it too often to keep the network stable.</small>',
          callback: () => {
            this.host.stop().catch(error => {
              this.$emit('error', error.message)
            })
          },
        })
      },
      updateSystem() {
        return this.$modals.confirm.open({
          text: 'You are going to update D2C system utils.',
          callback: () => {
            this.host.setup().catch(() => {})
          },
        })
      },
      destroy() {
        return this.$modals.confirm.open({
          callback: () => {
            utils.reachGoal('HOST_CARD_DESTROY_TRY')
            this.host.destroy().then(() => {
              utils.reachGoal('HOST_CARD_DESTROY_SUCCESS')
            }).catch(() => {
              utils.reachGoal('HOST_CARD_DESTROY_FAIL')
            })
          },
        })
      },
    },
  }
</script>

<style scoped>
  .provider-info { white-space: nowrap; overflow: hidden; }
  .right.floated.logo { margin: -.4em!important; height: 3.5em; }
  .containers { display: flex; align-items: center; }
  .containers-title { flex: 1; }
  .label.disabled { opacity: .2!important; }
  [data-tooltip]:hover:before, [data-tooltip]:hover:after { transition-delay: 300ms!important; }
  .host.node { background-color: rgba(63, 165, 165, .1)!important; }
  .host.master { background-color: rgba(100, 53, 201, .1)!important; }
  .host.slave { background-color: rgba(165, 103, 63, .1)!important; }
  .ports { display: flex; flex: 1!important; flex-wrap: wrap; font-size: .9em!important; font-family: monospace; }
  .port { display: flex; align-items: center; margin-right: 1em; white-space: nowrap; }
  .port.busy { color: #f00!important; }
  .button.dropdown { flex-grow: 0!important; border-radius: 0!important; }
  .right.floated.buttons { position: absolute; top: 0; right: 0; }
  .right.floated.buttons .button { margin-left: -1em; padding-right: 1.8em; border-radius: 0; border-bottom-left-radius: 1em; border-bottom: 1px solid #eee; border-left: 1px solid #eee; background-color: #f3f3f3; box-shadow: none!important; }
  .right.floated.buttons .button:hover { background-color: #eee; }
  .right.floated.buttons .button:last-child { padding-right: 0.785714em; }
  .content:first-child > .right.floated.buttons .button:last-child { border-top-right-radius: 0.28571429rem; }
  .right.floated.buttons .button >>> .icon { margin-top: -1px!important; }
</style>
